import axios from './axios-json';
import { errorToast } from './toasts';
import { getCookie } from 'tiny-cookie';
import { trackUserLogout, getDatadogUser } from './lib/datadogRum';

const errorToastDuration = 3000;

const tokenDom = document.querySelector('meta[name=csrf-token]');
if (tokenDom) {
  axios.defaults.headers.common['X-CSRF-Token'] = tokenDom.getAttribute('content');
}

const jwtCookieName = window['__JWT_COOKIE_NAME__'] || 'jwt';
const jwtToken = getCookie(jwtCookieName);

if (jwtToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
} else if (window.auth) {
  axios.defaults.headers.common['X-User-Email'] = window.auth.email;
  axios.defaults.headers.common['Authorization'] = `Bearer ${window.auth.authentication_token}`;
}

function redirectToRoot() {
  // replace() keeps the dead authenticated page out of history, so the back
  // button doesn't bounce the user between sign-in and stale pages.
  window.location.replace('/');
}

// Pages restored from the back/forward cache never hit the server, so a user
// who signed out (or deleted their account) would briefly see the stale
// authenticated page until an API call fails with 401. Redirect right away
// when the session cookie that authenticated this page is gone.
window.addEventListener('pageshow', event => {
  if (event.persisted && jwtToken && !getCookie(jwtCookieName)) {
    redirectToRoot();
  }
});

function authenticationErrorHandler() {
  const currentUser = getDatadogUser();
  trackUserLogout(currentUser?.id);

  errorToast('You are not authenticated! Redirecting to login page....', {
    duration: errorToastDuration,
  });
  redirectToRoot();

  // The page is navigating away; keep callers pending so they don't run
  // success/error handlers against a response that will never come.
  return new Promise(() => {});
}

function authorizationErrorHandler() {
  errorToast('You are not authorized to perform this action.', {
    duration: errorToastDuration,
    onComplete() {
      redirectToRoot();
    },
  });

  // Returning undefined would resolve the request promise and let callers run
  // success handlers against a missing response. Keep them pending instead;
  // the page redirects once the toast completes.
  return new Promise(() => {});
}

// Interceptors

axios.interceptors.request.use(
  config => {
    const latitude = sessionStorage.getItem('latitude');
    const longitude = sessionStorage.getItem('longitude');

    if (latitude && longitude) {
      config.headers = { ...config.headers, 'x-latitude': latitude, 'x-longitude': longitude };
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.config?.skipAuthErrorHandling) {
      return Promise.reject(error);
    }

    switch (error.response?.status) {
      case 401:
        return authenticationErrorHandler();
      case 403:
        return authorizationErrorHandler();
      default:
        return Promise.reject(error);
    }
  }
);

export default axios;
